import { useEffect, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { useRole } from "@/lib/roles";
import { BadgeCheck, KeyRound, Loader2, AlertTriangle, Search } from "lucide-react";

export default function MetaRegisterCard() {
  const { isAdmin } = useRole();
  const [wa, setWa] = useState(null);
  const [pin, setPin] = useState("");
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState(null); // {type, text}
  const [numbers, setNumbers] = useState(null); // null=not loaded, []=none
  const [loadingNums, setLoadingNums] = useState(false);

  const load = () => api.get("/organizations/me/whatsapp-settings").then(({ data }) => setWa(data)).catch(() => {});
  useEffect(() => { load(); }, []);

  if (!wa) return null;
  const provider = wa.whatsapp_provider || "meta_cloud";
  if (provider !== "meta_cloud") return null;
  const registered = !!wa.registered;
  const currentPnid = wa.phone_number_id;

  const checkNumbers = async () => {
    setLoadingNums(true); setMsg(null);
    try {
      const { data } = await api.get("/organizations/me/whatsapp/meta-phone-numbers");
      setNumbers(data.numbers || []);
    } catch (e) {
      setMsg({ type: "err", text: formatApiError(e.response?.data?.detail) || "Falha ao obter números." });
    } finally { setLoadingNums(false); }
  };

  const selectNumber = async (n) => {
    setBusy(true); setMsg(null);
    try {
      await api.post("/organizations/me/whatsapp/meta-select-number", {
        phone_number_id: n.id, display_phone_number: n.display_phone_number,
      });
      setMsg({ type: "ok", text: "Número selecionado. Agora registe-o com o PIN abaixo." });
      setNumbers(null);
      await load();
    } catch (e) {
      setMsg({ type: "err", text: formatApiError(e.response?.data?.detail) || "Falha ao selecionar." });
    } finally { setBusy(false); }
  };

  const submit = async () => {
    if (!/^\d{6}$/.test(pin)) { setMsg({ type: "err", text: "O PIN tem de ter 6 dígitos." }); return; }
    setBusy(true); setMsg(null);
    try {
      const { data } = await api.post("/organizations/me/whatsapp/register", { pin });
      setMsg({ type: "ok", text: data.detail || "Número registado." });
      setPin("");
      await load();
    } catch (e) {
      setMsg({ type: "err", text: formatApiError(e.response?.data?.detail) || "Falha no registo." });
    } finally { setBusy(false); }
  };

  return (
    <div className="rounded-2xl border border-zinc-200 bg-white p-6 shadow-sm space-y-4" data-testid="meta-register-card">
      <div className="flex items-center gap-2.5">
        <div className="w-9 h-9 rounded-lg bg-zinc-950 text-white flex items-center justify-center"><KeyRound className="w-4.5 h-4.5" /></div>
        <div>
          <div className="font-display text-lg font-semibold text-zinc-950">Registo do número na Meta</div>
          <div className="text-xs text-zinc-500">Passo obrigatório da Cloud API para poder enviar mensagens</div>
        </div>
      </div>

      {registered ? (
        <div className="flex items-center gap-2 text-sm text-emerald-700 bg-emerald-50 border border-emerald-200 rounded-lg px-3 py-2" data-testid="meta-registered-ok">
          <BadgeCheck className="w-4 h-4" /> Número registado na Cloud API.
        </div>
      ) : (
        <div className="flex items-start gap-2 text-sm text-amber-800 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2">
          <AlertTriangle className="w-4 h-4 mt-0.5 shrink-0" />
          <span>O número está ligado mas <strong>ainda não foi registado</strong> na Cloud API — por isso as mensagens ficam em fila.</span>
        </div>
      )}

      {isAdmin ? (
        <>
          {registered && (
            <p className="text-[11px] text-zinc-500" data-testid="meta-reregister-note">
              O número já está registado. Podes na mesma <strong>verificar/trocar</strong> o número ou <strong>voltar a registar</strong> abaixo se precisares (ex.: apontar para outro número ou repor o registo).
            </p>
          )}
          {/* Step 1: confirm the correct number on the WABA (fixes stale phone_number_id / Meta #33) */}
          <div className="rounded-lg border border-zinc-200 p-3 space-y-2">
            <div className="flex items-center justify-between gap-2 flex-wrap">
              <div className="text-sm font-medium text-zinc-900">1. Confirmar o número na Meta</div>
              <button onClick={checkNumbers} disabled={loadingNums} data-testid="meta-check-numbers-btn"
                className="inline-flex items-center gap-1.5 text-xs border border-zinc-300 rounded-lg px-2.5 py-1.5 hover:bg-zinc-50 disabled:opacity-50">
                {loadingNums ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Search className="w-3.5 h-3.5" />} Verificar números
              </button>
            </div>
            {numbers !== null && (
              numbers.length === 0 ? (
                <div className="text-xs text-red-600" data-testid="meta-no-numbers">Nenhum número encontrado nesta conta Meta (WABA). Verifique a conta ou o token.</div>
              ) : (
                <div className="space-y-1.5" data-testid="meta-numbers-list">
                  {numbers.map((n) => (
                    <div key={n.id} className="flex items-center justify-between gap-2 flex-wrap text-sm border border-zinc-100 rounded-md px-2.5 py-1.5">
                      <div>
                        <span className="font-medium text-zinc-900">{n.display_phone_number || n.verified_name || n.id}</span>
                        <span className="text-[11px] text-zinc-400 ml-2">ID {n.id}{n.status ? ` · ${n.status}` : ""}</span>
                        {String(n.id) === String(currentPnid) && <span className="ml-2 text-[10px] px-1.5 py-0.5 rounded-full bg-zinc-100 text-zinc-600">atual</span>}
                      </div>
                      <button onClick={() => selectNumber(n)} disabled={busy} data-testid={`meta-use-number-${n.id}`}
                        className="text-xs bg-zinc-950 text-white rounded-lg px-3 py-1.5 disabled:opacity-50">Usar este</button>
                    </div>
                  ))}
                </div>
              )
            )}
          </div>

          {/* Step 2: register with PIN */}
          <div className="rounded-lg border border-zinc-200 p-3 space-y-2">
            <div className="text-sm font-medium text-zinc-900">2. Registar com PIN de 6 dígitos</div>
            <div className="flex items-center gap-3 flex-wrap">
              <input inputMode="numeric" maxLength={6} value={pin}
                onChange={(e) => setPin(e.target.value.replace(/\D/g, "").slice(0, 6))}
                placeholder="PIN 6 dígitos"
                className="w-36 border border-zinc-300 rounded-lg px-3 py-2 text-sm tracking-widest"
                data-testid="meta-register-pin" />
              <button onClick={submit} disabled={busy} data-testid="meta-register-btn"
                className="inline-flex items-center gap-2 bg-zinc-950 text-white text-sm font-medium rounded-lg px-4 py-2 disabled:opacity-50">
                {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <KeyRound className="w-4 h-4" />} Registar número
              </button>
            </div>
            <p className="text-[11px] text-zinc-400">O PIN é a verificação em 2 passos da Meta. Se ainda não definiu nenhum, este passa a ser o seu PIN.</p>
          </div>
        </>
      ) : (
        <div className="text-xs text-zinc-500">Só administradores podem registar o número.</div>
      )}
      {msg && (
        <div className={`text-xs ${msg.type === "ok" ? "text-emerald-600" : "text-red-600"}`} data-testid="meta-register-msg">{msg.text}</div>
      )}
    </div>
  );
}
